import { ListChecks, Plus, Trash2 } from 'lucide-react'
import { type FormEvent, useMemo, useState } from 'react'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { Modal } from '@/components/ui/Modal'
import { PageHeader } from '@/components/ui/PageHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { Tooltip } from '@/components/ui/Tooltip'
import { Veld } from '@/components/ui/Veld'
import { formatUren } from '@/lib/format'
import { useMedewerkers } from '@/lib/medewerker-api'
import { useSemesterKeuze } from '@/lib/semester-context'
import { cn } from '@/lib/utils'
import { useMaakTaak, useTaken, useVerwijderTaak, useZetTaakUren } from '@/lib/taak-api'
import type { TaakRij } from '@/lib/taak-types'

interface Kolom {
  id: string
  naam: string
}

export function TakenPage() {
  const { semesterId, isLoading: semesterLaadt } = useSemesterKeuze()
  const taken = useTaken(semesterId)
  const medewerkers = useMedewerkers(true)
  const zetUren = useZetTaakUren(semesterId)
  const maakTaak = useMaakTaak(semesterId)
  const [nieuweTaak, setNieuweTaak] = useState(false)

  const laadt = semesterLaadt || taken.isLoading || medewerkers.isLoading

  const kolommen = useMemo<Kolom[]>(() => {
    const lijst = (medewerkers.data ?? []).map((medewerker) => ({
      id: medewerker.id,
      naam: medewerker.naam,
    }))
    const bekend = new Set(lijst.map((kolom) => kolom.id))
    const extra = new Set<string>()

    for (const taak of taken.data?.taken ?? []) {
      for (const id of Object.keys(taak.uren)) {
        if (!bekend.has(id)) extra.add(id)
      }
    }

    return [...lijst, ...[...extra].map((id) => ({ id, naam: 'Niet meer actief' }))]
  }, [medewerkers.data, taken.data])

  return (
    <div className="space-y-6">
      <PageHeader
        title="Taken"
        description="De uren per taak en per medewerker in dit semester. Een leeg vak betekent geen toewijzing."
        actions={
          <button type="button" className="btn-primary" onClick={() => setNieuweTaak(true)}>
            <Plus className="h-4 w-4" />
            Taak toevoegen
          </button>
        }
      />

      {taken.isError ? (
        <ErrorState error={taken.error} onRetry={() => taken.refetch()} />
      ) : laadt ? (
        <div className="space-y-6">
          <Skeleton className="h-96 w-full rounded-card" />
          <Skeleton className="h-48 w-full rounded-card" />
        </div>
      ) : !taken.data || taken.data.taken.length === 0 ? (
        <EmptyState
          icon={ListChecks}
          title="Nog geen taken"
          description="Voeg de taken toe die naast de lessen verdeeld worden."
          action={
            <button type="button" className="btn-primary" onClick={() => setNieuweTaak(true)}>
              <Plus className="h-4 w-4" />
              Taak toevoegen
            </button>
          }
        />
      ) : (
        <Takenmatrix
          taken={taken.data.taken}
          kolommen={kolommen}
          semesterId={semesterId}
          onZetUren={(taakId, medewerkerId, uren) => zetUren.mutate({ taakId, medewerkerId, uren })}
        />
      )}

      <NieuweTaakModal
        open={nieuweTaak}
        onOpenChange={setNieuweTaak}
        bezig={maakTaak.isPending}
        onBewaar={(input) =>
          maakTaak.mutate(input, { onSuccess: () => setNieuweTaak(false) })
        }
      />
    </div>
  )
}

interface TakenmatrixProps {
  taken: TaakRij[]
  kolommen: Kolom[]
  semesterId: string
  onZetUren: (taakId: string, medewerkerId: string, uren: number) => void
}

function Takenmatrix({ taken, kolommen, semesterId, onZetUren }: TakenmatrixProps) {
  const verwijderTaak = useVerwijderTaak(semesterId)

  const kolomtotalen = kolommen.map((kolom) =>
    taken.reduce((som, taak) => som + (taak.uren[kolom.id] ?? 0), 0),
  )
  const eindtotaal = taken.reduce((som, taak) => som + taak.totaal, 0)

  return (
    <section className="overflow-hidden rounded-card bg-surface shadow-card">
      <div className="overflow-x-auto">
        <table className="min-w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 z-20 min-w-[14rem] border-b border-hairline bg-surface px-4 py-2.5 text-left">
                <span className="label-caps">Taak</span>
              </th>
              {kolommen.map((kolom) => (
                <th key={kolom.id} className="border-b border-hairline bg-surface px-2 py-2.5">
                  <Tooltip content={kolom.naam}>
                    <span className="label-caps block w-[5.5rem] truncate text-right">
                      {kolom.naam}
                    </span>
                  </Tooltip>
                </th>
              ))}
              <th className="border-b border-hairline bg-surface px-4 py-2.5 text-right">
                <span className="label-caps">Totaal</span>
              </th>
            </tr>
          </thead>

          <tbody>
            {taken.map((taak) => (
              <tr key={taak.id} className="group">
                <th className="sticky left-0 z-10 border-b border-hairline bg-surface px-4 py-1.5 text-left font-normal">
                  <span className="flex items-center justify-between gap-2">
                    <span className="min-w-0">
                      <span
                        className={
                          taak.actief ? 'block truncate text-ink' : 'block truncate text-ink-faint'
                        }
                      >
                        {taak.naam}
                      </span>
                      {taak.sleutelOmschrijving ? (
                        <span className="block truncate text-xs text-ink-faint">
                          {taak.sleutelOmschrijving}
                        </span>
                      ) : null}
                    </span>
                    <button
                      type="button"
                      className="btn-ghost h-7 w-7 shrink-0 p-0 opacity-0 transition-opacity duration-150 group-hover:opacity-100"
                      aria-label={`${taak.naam} verwijderen`}
                      onClick={() => verwijderTaak.mutate(taak.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.85} />
                    </button>
                  </span>
                </th>

                {kolommen.map((kolom) => {
                  const waarde = taak.uren[kolom.id] ?? 0
                  return (
                    <td key={kolom.id} className="border-b border-hairline px-2 py-1">
                      <UrenCel
                        key={`${taak.id}-${kolom.id}-${waarde}`}
                        waarde={waarde}
                        label={`${taak.naam} voor ${kolom.naam}`}
                        onBewaar={(uren) => onZetUren(taak.id, kolom.id, uren)}
                      />
                    </td>
                  )
                })}

                <td className="num border-b border-hairline px-4 py-1.5 text-ink-soft">
                  {taak.totaal > 0 ? formatUren(taak.totaal) : ''}
                </td>
              </tr>
            ))}
          </tbody>

          <tfoot>
            <tr>
              <th className="sticky left-0 z-10 bg-surface px-4 py-2.5 text-left text-sm font-medium text-ink">
                Totaal
              </th>
              {kolomtotalen.map((totaal, index) => (
                <td key={kolommen[index].id} className="px-2 py-2.5">
                  <span className="num block w-[5.5rem] text-ink-soft">
                    {totaal > 0 ? formatUren(totaal) : ''}
                  </span>
                </td>
              ))}
              <td className="num px-4 py-2.5 font-semibold text-ink">{formatUren(eindtotaal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  )
}

function toonUren(waarde: number): string {
  return waarde === 0 ? '' : String(waarde).replace('.', ',')
}

interface UrenCelProps {
  waarde: number
  label: string
  onBewaar: (uren: number) => void
}

/**
 * Een cel in de takenmatrix is een invoerveld en hoort er ook zo uit te zien.
 * Een rand die pas bij hover verschijnt maakt een tabel die je kunt invullen
 * ononderscheidbaar van een tabel die je alleen kunt lezen.
 */
function UrenCel({ waarde, label, onBewaar }: UrenCelProps) {
  const gevuld = waarde !== 0

  return (
    <input
      aria-label={label}
      defaultValue={toonUren(waarde)}
      inputMode="decimal"
      className={cn(
        'num h-8 w-[5.5rem] rounded-md border px-2 text-sm transition-colors duration-150',
        'focus:border-primair-500/60 focus:bg-surface focus:outline-hidden focus:ring-2 focus:ring-primair-500/25',
        gevuld
          ? 'border-hairline bg-surface font-medium text-ink hover:border-hairline-strong'
          : 'border-hairline bg-fill-1 text-ink hover:border-hairline-strong hover:bg-surface',
      )}
      onKeyDown={(gebeurtenis) => {
        if (gebeurtenis.key === 'Enter') gebeurtenis.currentTarget.blur()
      }}
      onBlur={(gebeurtenis) => {
        const tekst = gebeurtenis.target.value.trim().replace(',', '.')
        const nieuw = tekst === '' ? 0 : Number(tekst)
        if (Number.isNaN(nieuw) || nieuw < 0) {
          gebeurtenis.target.value = toonUren(waarde)
          return
        }
        if (nieuw !== waarde) onBewaar(nieuw)
      }}
    />
  )
}

interface NieuweTaakModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  bezig: boolean
  onBewaar: (input: { naam: string; sleutelOmschrijving: string | null }) => void
}

function NieuweTaakModal({ open, onOpenChange, bezig, onBewaar }: NieuweTaakModalProps) {
  const [naam, setNaam] = useState('')
  const [sleutel, setSleutel] = useState('')

  function verstuur(gebeurtenis: FormEvent) {
    gebeurtenis.preventDefault()
    if (naam.trim() === '') return
    onBewaar({ naam: naam.trim(), sleutelOmschrijving: sleutel.trim() || null })
  }

  return (
    <Modal
      open={open}
      onOpenChange={(volgende) => {
        onOpenChange(volgende)
        if (!volgende) {
          setNaam('')
          setSleutel('')
        }
      }}
      title="Taak toevoegen"
      description="Taken gelden voor alle semesters, de uren leg je per semester vast."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={() => onOpenChange(false)}>
            Annuleren
          </button>
          <button type="submit" form="nieuwe-taak" className="btn-primary" disabled={bezig}>
            Toevoegen
          </button>
        </>
      }
    >
      <form id="nieuwe-taak" onSubmit={verstuur} className="space-y-4">
        <Veld label="Naam">
          <input
            className="field"
            value={naam}
            onChange={(gebeurtenis) => setNaam(gebeurtenis.target.value)}
            placeholder="Examencommissie"
            required
          />
        </Veld>
        <Veld label="Sleutel" hint="Bijvoorbeeld hoe de uren zijn afgesproken.">
          <input
            className="field"
            value={sleutel}
            onChange={(gebeurtenis) => setSleutel(gebeurtenis.target.value)}
            placeholder="2 uur per lesweek"
          />
        </Veld>
      </form>
    </Modal>
  )
}

